import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { CanonicalTag } from '@/components/common/CanonicalTag';
import { getDealerships } from '@/config/dealerships.config';
import { getVehicles } from '@/config/vehicles.config';
import { getComparisons } from '@/config/comparisons.config';
import { getResearch } from '@/config/research.config';
import { getFAQs } from '@/config/faqs.config';
import { getServices } from '@/config/services.config';

interface TopicsPageProps {
    showBanner: () => void;
}

export const TopicsPage: React.FC<TopicsPageProps> = ({ showBanner }) => {
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat();

    const sections = useMemo(() => [
        { label: 'Dealerships', path: '/dealerships', show: getDealerships().length > 0, description: 'Browse dealership locations.' },
        { label: 'Vehicles', path: '/vehicles', show: getVehicles().length > 0, description: 'Explore vehicles.' },
        { label: 'Research Guides', path: '/research', show: getResearch().length > 0, description: 'Research SUVs, trucks, and cars before you buy.' },
        { label: 'Vehicle Comparisons', path: '/compare', show: getComparisons().length > 0, description: 'Compare models side by side.' },
        { label: 'FAQ', path: '/faq', show: getFAQs().length > 0, description: 'Answers to common vehicle questions.' },
        { label: 'Services', path: '/services', show: getServices().length > 0, description: 'Insurance, financing, and other vehicle services.' },
    ].filter(s => s.show), []);

    const chatSuggestions = [
        'Show me vehicles available near me',
        'What dealership is closest to me?',
        'Help me compare two vehicles',
        'What SUV is best for my family?',
    ];

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
            </>
        );
    }

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/topics" />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-6">
                                Topics
                            </h1>

                            <div className="space-y-3">
                                {sections.map((s, i) => (
                                    <Link
                                        key={i}
                                        to={s.path}
                                        className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-[#1a1a1a] border border-gray-700 hover:border-cyan-500 transition-colors group"
                                    >
                                        <div>
                                            <p className="text-sm sm:text-base font-semibold text-white group-hover:text-cyan-400 transition-colors">
                                                {s.label}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{s.description}</p>
                                        </div>
                                        <span className="text-gray-500 group-hover:text-cyan-400 transition-colors text-lg ml-4">→</span>
                                    </Link>
                                ))}
                            </div>

                            {/* Chat CTA */}
                            <QuickSuggestionsBlock
                                topLine="Have a question?"
                                title="Chat with us"
                                suggestions={chatSuggestions}
                                onSend={sendMessage}
                            />

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
